/**
 * Henter ALLE pris-input for det aktive firma:
 *   - companies (default_discount_pct, access_mode)
 *   - company_variant_prices (firma-overrides)
 *   - company_price_packages → package_variant_prices (+ mængde-trin fra
 *     package_variant_price_tiers)
 *   - company_product_access (adgangs-/rabatregler) FLETTET med
 *     package_product_access (+ trin-rabatter fra package_access_discount_tiers)
 *   - company_variant_cashback (cashback-%) + package_cashback_rules
 *
 * Selve prisopløsningen sker i src/domain (useEffectivePrice).
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase/client";
import { fetchAllPages } from "@/lib/supabase/fetchAll";
import type { AccessRule, CompanyVariantPrice, PackagePrice, QuantityDiscountTier } from "@/domain";
import { useCompanyById } from "./useActiveCompany";
import { useActiveCompanyId } from "@/features/sales/ImpersonationContext";

/** Pakke-cashbackregel (package_cashback_rules) til optjeningsvisning. */
export interface PackageCashbackRule {
  accessType: "PRODUCT" | "COLLECTION";
  shopifyProductId: string | null;
  shopifyCollectionId: string | null;
  cashbackPct: number;
}

export interface PriceData {
  defaultDiscountPct: number;
  accessMode: "ALL" | "SELECTED";
  companyVariantPrices: CompanyVariantPrice[];
  packagePrices: PackagePrice[];
  /** Firma-regler + pakke-regler (pakke-regler kan have discountTiers). */
  accessRules: AccessRule[];
  /** variantId → cashback-% (company_variant_cashback — vinder pr. variant). */
  cashbackPctByVariant: Map<string, number>;
  /** Pakkernes cashback-regler (PRODUCT/COLLECTION) til optjeningsvisning. */
  packageCashbackRules: PackageCashbackRule[];
  isLoading: boolean;
}

export function usePriceData(companyId: string | null): PriceData {
  const companyQuery = useCompanyById(companyId);

  const companyPricesQuery = useQuery({
    queryKey: ["firmapriser", companyId],
    queryFn: async () => {
      return fetchAllPages(async (from, to) => {
        const { data, error } = await supabase
          .from("company_variant_prices")
          .select("shopify_variant_id, price, min_order_qty, pack_size")
          .eq("company_id", companyId as string)
          .order("shopify_variant_id")
          .range(from, to);
        if (error) throw error;
        return data ?? [];
      });
    },
    enabled: !!companyId,
  });

  const packageIdsQuery = useQuery({
    queryKey: ["firmapakker", companyId],
    queryFn: async () => {
      // KUN pakker der er tildelt DETTE firma — og kun aktive pakker.
      // (inner join på price_packages så deaktiverede pakker aldrig
      // påvirker priser/rabatter/cashback.)
      const { data, error } = await supabase
        .from("company_price_packages")
        .select("package_id, price_packages!inner(is_active)")
        .eq("company_id", companyId as string)
        .eq("price_packages.is_active", true);
      if (error) throw error;
      return (data ?? []).map((r) => r.package_id);
    },
    enabled: !!companyId,
  });

  const packageIds = packageIdsQuery.data;
  const packagePricesQuery = useQuery({
    queryKey: ["pakkepriser", companyId, packageIds],
    queryFn: async () => {
      if (!packageIds || packageIds.length === 0) return [];
      return fetchAllPages(async (from, to) => {
        const { data, error } = await supabase
          .from("package_variant_prices")
          .select("package_id, shopify_variant_id, price, min_order_qty, pack_size")
          .in("package_id", packageIds)
          .order("shopify_variant_id")
          .range(from, to);
        if (error) throw error;
        return data ?? [];
      });
    },
    enabled: packageIds != null,
  });

  // Mængde-trin for pakke-variantpriser (stykpris fra min_qty)
  const packagePriceTiersQuery = useQuery({
    queryKey: ["pakkepris-trin", companyId, packageIds],
    queryFn: async () => {
      if (!packageIds || packageIds.length === 0) return [];
      return fetchAllPages(async (from, to) => {
        const { data, error } = await supabase
          .from("package_variant_price_tiers")
          .select("package_id, shopify_variant_id, min_qty, price")
          .in("package_id", packageIds)
          .order("shopify_variant_id")
          .range(from, to);
        if (error) throw error;
        return data ?? [];
      });
    },
    enabled: packageIds != null,
  });

  const accessRulesQuery = useQuery({
    queryKey: ["produktadgang", companyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("company_product_access")
        .select("access_type, shopify_product_id, shopify_collection_id, discount_pct")
        .eq("company_id", companyId as string);
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!companyId,
  });

  // Pakke-adgangsregler MED trin-rabatter (de seedede pakkerabatter skal
  // faktisk slå igennem i prisopløsningen — ikke kun i adgangsfiltreringen).
  const packageAccessQuery = useQuery({
    queryKey: ["pakke-rabatregler", companyId, packageIds],
    queryFn: async () => {
      if (!packageIds || packageIds.length === 0) return [];
      const { data, error } = await supabase
        .from("package_product_access")
        .select(
          "id, access_type, shopify_product_id, shopify_collection_id, discount_pct, package_access_discount_tiers(min_qty, discount_pct)",
        )
        .in("package_id", packageIds);
      if (error) throw error;
      return data ?? [];
    },
    enabled: packageIds != null,
  });

  const cashbackRulesQuery = useQuery({
    queryKey: ["cashback-regler", companyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("company_variant_cashback")
        .select("shopify_variant_id, cashback_pct")
        .eq("company_id", companyId as string);
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!companyId,
  });

  const packageCashbackQuery = useQuery({
    queryKey: ["pakke-cashback", companyId, packageIds],
    queryFn: async () => {
      if (!packageIds || packageIds.length === 0) return [];
      const { data, error } = await supabase
        .from("package_cashback_rules")
        .select("access_type, shopify_product_id, shopify_collection_id, cashback_pct")
        .in("package_id", packageIds);
      if (error) throw error;
      return data ?? [];
    },
    enabled: packageIds != null,
  });

  return useMemo<PriceData>(() => {
    const companyVariantPrices: CompanyVariantPrice[] = (companyPricesQuery.data ?? []).map(
      (r) => ({
        shopifyVariantId: r.shopify_variant_id,
        price: Number(r.price),
        minOrderQty: r.min_order_qty ?? 1,
        packSize: r.pack_size ?? 1,
      }),
    );

    // Trin pr. (pakke, variant)
    const tiersByPackageVariant = new Map<string, { minQty: number; price: number }[]>();
    for (const t of packagePriceTiersQuery.data ?? []) {
      const key = `${t.package_id}:${t.shopify_variant_id}`;
      const list = tiersByPackageVariant.get(key);
      const tier = { minQty: Number(t.min_qty), price: Number(t.price) };
      if (list) list.push(tier);
      else tiersByPackageVariant.set(key, [tier]);
    }

    const packagePrices: PackagePrice[] = (packagePricesQuery.data ?? []).map((r) => ({
      packageId: r.package_id,
      shopifyVariantId: r.shopify_variant_id,
      price: Number(r.price),
      minOrderQty: r.min_order_qty ?? 1,
      packSize: r.pack_size ?? 1,
      tiers: tiersByPackageVariant.get(`${r.package_id}:${r.shopify_variant_id}`),
    }));

    const companyRules: AccessRule[] = (accessRulesQuery.data ?? []).map((r) => ({
      accessType: r.access_type === "COLLECTION" ? "COLLECTION" : "PRODUCT",
      shopifyProductId: r.shopify_product_id,
      shopifyCollectionId: r.shopify_collection_id,
      discountPct: Number(r.discount_pct ?? 0),
    }));
    const packageRules: AccessRule[] = (packageAccessQuery.data ?? []).map((r) => {
      const discountTiers: QuantityDiscountTier[] = (r.package_access_discount_tiers ?? []).map(
        (t) => ({ minQty: Number(t.min_qty), discountPct: Number(t.discount_pct) }),
      );
      return {
        accessType: r.access_type === "COLLECTION" ? "COLLECTION" : "PRODUCT",
        shopifyProductId: r.shopify_product_id,
        shopifyCollectionId: r.shopify_collection_id,
        discountPct: Number(r.discount_pct ?? 0),
        discountTiers: discountTiers.length > 0 ? discountTiers : undefined,
      };
    });
    const accessRules: AccessRule[] = [...companyRules, ...packageRules];

    const cashbackPctByVariant = new Map<string, number>();
    for (const r of cashbackRulesQuery.data ?? []) {
      const pct = Number(r.cashback_pct);
      if (pct > 0) cashbackPctByVariant.set(r.shopify_variant_id, pct);
    }

    const packageCashbackRules: PackageCashbackRule[] = (packageCashbackQuery.data ?? [])
      .map((r) => ({
        accessType: (r.access_type === "COLLECTION" ? "COLLECTION" : "PRODUCT") as
          | "PRODUCT"
          | "COLLECTION",
        shopifyProductId: r.shopify_product_id,
        shopifyCollectionId: r.shopify_collection_id,
        cashbackPct: Number(r.cashback_pct),
      }))
      .filter((r) => r.cashbackPct > 0);

    return {
      defaultDiscountPct: Number(companyQuery.data?.default_discount_pct ?? 0),
      accessMode: companyQuery.data?.access_mode === "SELECTED" ? "SELECTED" : "ALL",
      companyVariantPrices,
      packagePrices,
      accessRules,
      cashbackPctByVariant,
      packageCashbackRules,
      isLoading:
        !!companyId &&
        (companyQuery.isLoading ||
          companyPricesQuery.isLoading ||
          packageIdsQuery.isLoading ||
          packagePricesQuery.isLoading ||
          packagePriceTiersQuery.isLoading ||
          accessRulesQuery.isLoading ||
          packageAccessQuery.isLoading ||
          cashbackRulesQuery.isLoading ||
          packageCashbackQuery.isLoading),
    };
  }, [
    companyId,
    companyQuery.data,
    companyQuery.isLoading,
    companyPricesQuery.data,
    companyPricesQuery.isLoading,
    packageIdsQuery.isLoading,
    packagePricesQuery.data,
    packagePricesQuery.isLoading,
    packagePriceTiersQuery.data,
    packagePriceTiersQuery.isLoading,
    accessRulesQuery.data,
    accessRulesQuery.isLoading,
    packageAccessQuery.data,
    packageAccessQuery.isLoading,
    cashbackRulesQuery.data,
    cashbackRulesQuery.isLoading,
    packageCashbackQuery.data,
    packageCashbackQuery.isLoading,
  ]);
}

/** Bekvem variant der selv finder det aktive firma. */
export function useActivePriceData(): PriceData & { companyId: string | null } {
  const companyId = useActiveCompanyId();
  const data = usePriceData(companyId);
  return { ...data, companyId };
}
